// admin.onlytrade.ai/certificates — Certificate type registry UI.
// Mirrors Calculators.tsx but for the cert/forms catalogue defined in
// cert-configs.js (CERT_META). Lets admins rename certs, retag their trade,
// gate by tier, leave an internal note, or turn them off without a deploy.
//
// Hits /api/admin/cert-types (proxied to OnlyTrade :3004 like the calc admin).
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  certTypes as certApi,
  type AdminCertType,
  type CertTradeSummary,
  type CertTier,
} from '../api';

// Trade labels for the cert UI. Distinct from the calc admin's TRADE_LABELS:
// certs split by gas/commercial/specialist rather than the 16-trade DPS taxonomy.
const TRADE_LABELS: Record<string, string> = {
  gas:           '🔥 Gas Engineer',
  plumber:       '🔧 Plumber',
  heating:       '🌡️ Heating Eng.',
  electrician:   '⚡ Electrician',
  lpg:           '🛢️ LPG',
  commercial:    '🏭 Commercial',
  specialist:    '🔬 Specialist',
  investigation: '⚠️ Investigation',
  universal:     '🔧 Universal',
};

export default function CertsPage() {
  const [certs, setCerts] = useState<AdminCertType[] | null>(null);
  const [summary, setSummary] = useState<CertTradeSummary[]>([]);
  const [tiers, setTiers] = useState<CertTier[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tradeFilter, setTradeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled' | 'gated'>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminCertType | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await certApi.list();
      setCerts(d.cert_types);
      setSummary(d.summary);
      setTiers(d.tiers);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    if (!certs) return [];
    const q = search.trim().toLowerCase();
    return certs.filter(c => {
      if (tradeFilter !== 'all') {
        const inPrimary = c.trade_key === tradeFilter;
        const inSecondary = Array.isArray(c.secondary_trades) && c.secondary_trades.includes(tradeFilter);
        if (!inPrimary && !inSecondary) return false;
      }
      if (statusFilter === 'enabled' && !c.is_enabled) return false;
      if (statusFilter === 'disabled' && c.is_enabled) return false;
      if (statusFilter === 'gated' && !c.min_tier_id) return false;
      if (q) {
        const label = (c.display_name || c.code_label || '').toLowerCase();
        if (!c.cert_type.toLowerCase().includes(q) && !label.includes(q)) return false;
      }
      return true;
    });
  }, [certs, tradeFilter, statusFilter, search]);

  const totals = useMemo(() => {
    if (!certs) return { total: 0, enabled: 0, gated: 0 };
    return {
      total: certs.length,
      enabled: certs.filter(c => c.is_enabled).length,
      gated: certs.filter(c => c.min_tier_id).length,
    };
  }, [certs]);

  // Local recompute so toggles don't need a round-trip refresh — same trick
  // the calc admin uses. Each cert counts against primary + every secondary.
  function recalcSummary(list: AdminCertType[]): CertTradeSummary[] {
    const buckets = new Map<string, CertTradeSummary>();
    function bump(tk: string, c: AdminCertType) {
      const s = buckets.get(tk) || { trade_key: tk, total: 0, enabled: 0, gated: 0 };
      s.total++;
      if (c.is_enabled) s.enabled++;
      if (c.min_tier_id) s.gated++;
      buckets.set(tk, s);
    }
    for (const c of list) {
      bump(c.trade_key, c);
      for (const t of c.secondary_trades || []) bump(t, c);
    }
    return [...buckets.values()].sort((a, b) => a.trade_key.localeCompare(b.trade_key));
  }

  async function toggle(cert: AdminCertType, next: boolean) {
    setSavingId(cert.cert_type);
    setCerts(prev => {
      const updated = prev?.map(c => c.cert_type === cert.cert_type ? { ...c, is_enabled: next } : c) ?? null;
      if (updated) setSummary(recalcSummary(updated));
      return updated;
    });
    try {
      const fresh = await certApi.update(cert.cert_type, { is_enabled: next });
      setCerts(prev => {
        const updated = prev?.map(c => c.cert_type === fresh.cert_type ? fresh : c) ?? null;
        if (updated) setSummary(recalcSummary(updated));
        return updated;
      });
    } catch (e) {
      setError((e as Error).message);
      setCerts(prev => {
        const reverted = prev?.map(c => c.cert_type === cert.cert_type ? { ...c, is_enabled: cert.is_enabled } : c) ?? null;
        if (reverted) setSummary(recalcSummary(reverted));
        return reverted;
      });
    } finally { setSavingId(null); }
  }

  async function saveEdit(updates: Partial<AdminCertType>) {
    if (!editing) return;
    try {
      const fresh = await certApi.update(editing.cert_type, updates);
      setEditing(null);
      setCerts(prev => {
        const updated = prev?.map(c => c.cert_type === fresh.cert_type ? fresh : c) ?? null;
        if (updated) setSummary(recalcSummary(updated));
        return updated;
      });
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">Certificates &amp; Forms</h1>
          <p className="text-xs text-ink-muted mt-1">
            {totals.total} types · {totals.enabled} enabled · {totals.gated} tier-gated
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        {summary.map(s => {
          const isActive = tradeFilter === s.trade_key;
          return (
            <button
              key={s.trade_key}
              onClick={() => setTradeFilter(isActive ? 'all' : s.trade_key)}
              className={`text-left p-2.5 rounded border transition-colors ${
                isActive
                  ? 'bg-brand-600/20 border-brand-600'
                  : 'bg-ink-card border-ink-border hover:border-brand-600/50'
              }`}
            >
              <div className="text-[11px] font-semibold tracking-wide">
                {TRADE_LABELS[s.trade_key] || s.trade_key}
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                {s.enabled}/{s.total} on
                {s.gated > 0 && <span className="ml-1 text-amber-400" title="Tier-gated">· {s.gated} 🔒</span>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="text"
          placeholder="Search by cert id or name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] bg-ink-card border border-ink-border rounded px-3 py-2 text-sm"
        />
        <select
          value={tradeFilter}
          onChange={e => setTradeFilter(e.target.value)}
          className="bg-ink-card border border-ink-border rounded px-3 py-2 text-sm"
        >
          <option value="all">All trades</option>
          {summary.map(s => (
            <option key={s.trade_key} value={s.trade_key}>{TRADE_LABELS[s.trade_key] || s.trade_key}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
          className="bg-ink-card border border-ink-border rounded px-3 py-2 text-sm"
        >
          <option value="all">All status</option>
          <option value="enabled">Enabled only</option>
          <option value="disabled">Disabled only</option>
          <option value="gated">Tier-gated only</option>
        </select>
      </div>

      {certs === null && !error && (
        <div className="text-ink-muted text-sm">Loading cert types…</div>
      )}
      {certs && certs.length > 0 && (
        <div className="bg-ink-card border border-ink-border rounded overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-ink-border bg-ink-bg text-[11px] uppercase text-ink-muted font-semibold">
            <div className="col-span-1">On</div>
            <div className="col-span-3">Cert ID</div>
            <div className="col-span-3">Display name</div>
            <div className="col-span-2">Trade</div>
            <div className="col-span-2">Tier gate / note</div>
            <div className="col-span-1 text-right">Edit</div>
          </div>
          {filtered.map(c => {
            const tierName = c.min_tier_id
              ? (tiers.find(t => t.id === c.min_tier_id)?.name || `tier #${c.min_tier_id}`)
              : null;
            return (
              <div key={c.cert_type}
                   className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-ink-border last:border-b-0 items-center ${
                     c.is_enabled ? '' : 'opacity-60'
                   } hover:bg-ink-bg/50`}>
                <div className="col-span-1">
                  <button
                    disabled={savingId === c.cert_type}
                    onClick={() => toggle(c, !c.is_enabled)}
                    aria-label={c.is_enabled ? 'Turn off' : 'Turn on'}
                    className={`w-10 h-6 rounded-full relative transition-colors ${
                      c.is_enabled ? 'bg-green-600' : 'bg-ink-border'
                    } disabled:opacity-50`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      c.is_enabled ? 'left-[18px]' : 'left-0.5'
                    }`} />
                  </button>
                </div>
                <div className="col-span-3 text-xs font-mono text-ink-muted truncate" title={c.cert_type}>
                  {c.icon} {c.cert_type}
                </div>
                <div className="col-span-3 text-sm truncate">
                  {c.display_name || <span className="text-ink-muted italic">{c.code_label}</span>}
                </div>
                <div className="col-span-2 text-xs">
                  <div>{TRADE_LABELS[c.trade_key] || c.trade_key}</div>
                  {c.secondary_trades && c.secondary_trades.length > 0 && (
                    <div className="text-[10px] text-ink-muted mt-0.5 truncate" title={c.secondary_trades.join(', ')}>
                      + {c.secondary_trades.map(t => TRADE_LABELS[t] || t).join(', ')}
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-xs text-ink-muted truncate">
                  {tierName && <span className="text-amber-400">🔒 {tierName}</span>}
                  {tierName && c.admin_note && ' · '}
                  {c.admin_note && <span title={c.admin_note}>{c.admin_note}</span>}
                </div>
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => setEditing(c)}
                    className="text-xs text-brand-400 hover:text-brand-300"
                  >Edit</button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-ink-muted">No cert types match the filters.</div>
          )}
        </div>
      )}

      {editing && (
        <EditDrawer
          cert={editing}
          tiers={tiers}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}

// ─── Edit drawer ─────────────────────────────────────────────────────────────
function EditDrawer({ cert, tiers, onClose, onSave }: {
  cert: AdminCertType;
  tiers: CertTier[];
  onClose: () => void;
  onSave: (updates: Partial<AdminCertType>) => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(cert.display_name ?? '');
  const [adminNote, setAdminNote] = useState(cert.admin_note ?? '');
  const [tradeKey, setTradeKey] = useState(cert.trade_key);
  const [secondaryTrades, setSecondaryTrades] = useState<string[]>(cert.secondary_trades ?? []);
  const [minTierId, setMinTierId] = useState<number | null>(cert.min_tier_id);
  const [saving, setSaving] = useState(false);

  function toggleSecondary(key: string) {
    if (key === tradeKey) return;
    setSecondaryTrades(prev =>
      prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
    );
  }
  function changePrimary(key: string) {
    setTradeKey(key);
    setSecondaryTrades(prev => prev.filter(t => t !== key));
  }

  async function submit() {
    setSaving(true);
    try {
      await onSave({
        display_name: displayName.trim() || null,
        admin_note: adminNote.trim() || null,
        trade_key: tradeKey,
        secondary_trades: secondaryTrades,
        min_tier_id: minTierId,
      });
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-ink-border rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-ink-border">
          <div>
            <div className="text-xs text-ink-muted font-mono">{cert.icon} {cert.cert_type}</div>
            <div className="text-base font-semibold">{cert.display_name || cert.code_label}</div>
            <div className="text-xs text-ink-muted mt-1">
              {TRADE_LABELS[cert.trade_key] || cert.trade_key} · {cert.is_enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-text text-xl">×</button>
        </div>

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">Primary trade</label>
        <select
          value={tradeKey}
          onChange={e => changePrimary(e.target.value)}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        >
          {Object.entries(TRADE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">
          Also appears in <span className="text-ink-muted normal-case font-normal">(tap to toggle)</span>
        </label>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {Object.entries(TRADE_LABELS).map(([key, label]) => {
            const isPrimary = key === tradeKey;
            const isOn = secondaryTrades.includes(key);
            return (
              <button
                key={key}
                type="button"
                disabled={isPrimary}
                onClick={() => toggleSecondary(key)}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  isPrimary
                    ? 'bg-brand-600/20 border-brand-600 text-brand-300 cursor-not-allowed'
                    : isOn
                      ? 'bg-green-900/40 border-green-700 text-green-200'
                      : 'bg-ink-bg border-ink-border text-ink-muted hover:border-brand-600/50'
                }`}
              >
                {label}{isPrimary ? ' (primary)' : ''}
              </button>
            );
          })}
        </div>

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">Display name override</label>
        <input
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          placeholder={`Leave blank to use "${cert.code_label}"`}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        />

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">
          Tier gate <span className="text-ink-muted normal-case font-normal">(lowest tier required)</span>
        </label>
        <select
          value={minTierId === null ? '' : String(minTierId)}
          onChange={e => setMinTierId(e.target.value === '' ? null : Number(e.target.value))}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        >
          <option value="">No gate — every tier (incl. Free)</option>
          {tiers.map(t => (
            <option key={t.id} value={String(t.id)}>
              {t.name}
              {t.price_monthly && Number(t.price_monthly) > 0
                ? ` (£${(Number(t.price_monthly) / 100).toFixed(2)}/mo)`
                : ''}
            </option>
          ))}
        </select>

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">Admin note (internal)</label>
        <textarea
          value={adminNote}
          onChange={e => setAdminNote(e.target.value)}
          placeholder='e.g. "moved to Pro tier 2026-04-28 — premium template"'
          rows={2}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        />

        <div className="text-[11px] text-ink-muted mb-3">
          Last edit: {cert.modified_at ? new Date(cert.modified_at).toLocaleString() : '—'}
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm px-3 py-2 border border-ink-border rounded hover:bg-ink-bg">Cancel</button>
          <button
            disabled={saving}
            onClick={submit}
            className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded"
          >{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
